import { Workstation } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DropdownField, SearchField, useDebouncedValue, useFormField } from '@frontend/design-system';
import { ReducedOperatory } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useReviewRequest';
import { WorkstationDropdownList } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/types';

type Props = {
  disabled?: boolean;
  fieldProps: ReturnType<typeof DropdownField>['props'];
  isLoadingAvailableWorkstations?: boolean;
  showGroupedWorkstations?: boolean;
  workstationList: WorkstationDropdownList<Workstation | ReducedOperatory>;
};

export const WorkstationDropdownSearchList = ({
  disabled,
  fieldProps,
  showGroupedWorkstations,
  isLoadingAvailableWorkstations,
  workstationList,
}: Props) => {
  const { t } = useTranslation('schedule');

  const workstationSearchFieldProps = useFormField({ type: 'text' });
  const searchText = useDebouncedValue(workstationSearchFieldProps.value);

  const selectedWorkstationValue = fieldProps.value;
  const selectedWorkstation =
    workstationList.available.find(({ id }) => id === selectedWorkstationValue) ||
    workstationList.all.find(({ id }) => id === selectedWorkstationValue);

  const baseFilteredAvailableWorkstationList = searchText
    ? workstationList.available.filter(({ workstationName }) =>
        workstationName?.toLowerCase().includes(searchText.toLowerCase())
      )
    : workstationList.available;

  const baseFilteredAllWorkstationList = searchText
    ? workstationList.all.filter(({ workstationName }) =>
        workstationName?.toLowerCase().includes(searchText.toLowerCase())
      )
    : workstationList.all;

  const filteredAllWorkstationList =
    selectedWorkstation &&
    workstationList.all.find(({ id }) => id === selectedWorkstation.id) &&
    !baseFilteredAllWorkstationList.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredAllWorkstationList]
      : selectedWorkstation && workstationList.all.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredAllWorkstationList.filter(({ id }) => id !== selectedWorkstation.id)]
      : baseFilteredAllWorkstationList;

  const filteredAvailableWorkstationList =
    selectedWorkstation &&
    workstationList.available.find(({ id }) => id === selectedWorkstation.id) &&
    !baseFilteredAvailableWorkstationList.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredAvailableWorkstationList]
      : selectedWorkstation && workstationList.available.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredAvailableWorkstationList.filter(({ id }) => id !== selectedWorkstation.id)]
      : baseFilteredAvailableWorkstationList;

  const filteredWorkstationList = {
    available: filteredAvailableWorkstationList,
    all: filteredAllWorkstationList,
  };

  return (
    <DropdownField disabled={disabled} label={t('Workstation')} {...fieldProps}>
      <SearchField
        name='search-Workstation'
        {...workstationSearchFieldProps}
        css={{
          margin: theme.spacing(1, 2),
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.colors.white,
        }}
      />
      {searchText && filteredWorkstationList.all.length === 0 && filteredWorkstationList.available.length === 0 ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: t('No matching workstation names found'),
            description: '',
          }}
        />
      ) : showGroupedWorkstations ? (
        <DropdownField.OptionGroup label={t('Available')}>
          {filteredWorkstationList.available.length > 0 ? (
            filteredWorkstationList.available.map((workstation) => (
              <DropdownField.Option
                value={workstation.id ?? ''}
                key={workstation.id}
                disabled={'hidden' in workstation ? workstation.hidden : false}
              >
                {workstation.workstationName}
              </DropdownField.Option>
            ))
          ) : (
            <DropdownField.Option disabled value='no-available-workstation' key='no-available-workstation'>
              {isLoadingAvailableWorkstations ? t('Loading...') : t('There are no available workstations')}
            </DropdownField.Option>
          )}
        </DropdownField.OptionGroup>
      ) : null}
      {showGroupedWorkstations ? (
        <DropdownField.OptionGroup label={t('All Workstations')}>
          {filteredWorkstationList.all.map((workstation) => (
            <DropdownField.Option
              value={workstation.id ?? ''}
              key={workstation.id}
              disabled={'hidden' in workstation ? workstation.hidden : false}
            >
              {workstation.workstationName}
            </DropdownField.Option>
          ))}
        </DropdownField.OptionGroup>
      ) : (
        filteredWorkstationList.all.map((workstation) => (
          <DropdownField.Option
            value={workstation.id ?? ''}
            key={workstation.id}
            disabled={'hidden' in workstation ? workstation.hidden : false}
          >
            {workstation.workstationName}
          </DropdownField.Option>
        ))
      )}
    </DropdownField>
  );
};
